// Copyright ©️ 2026 eVolve MEP, LLC

import {
  getObjectPropertyValueAsNumber,
  getObjectPropertyValueAsString,
  type TypeSafePropertyResult,
} from 'components/Autodesk/extensions/propertyHelpers';
import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { strongEntries } from 'helpers/strongEntries';

/**
 * Spools themselves do not have a level, so this calculates the level of a spool based on its children.
 */
export const getMostFrequentLevel = (spoolObjects: RenderableSpool['objects']) => {
  const levelFrequency = spoolObjects.reduce(
    (curr, { level, referenceLevel }) => {
      // Prefer "level" over "referenceLevel", ignoring this object if both are blank.
      const usingLevel = level ?? referenceLevel;
      if (usingLevel) curr[usingLevel] = (curr[usingLevel] ?? 0) + 1;
      return curr;
    },
    {} as Record<string, number>,
  );
  const [mostFrequent] = strongEntries(levelFrequency).reduce(
    ([level, frequency], [currentLevel, currentFrequency]) => {
      if (currentFrequency > frequency) {
        return [currentLevel, currentFrequency];
      }
      return [level, frequency];
    },
    [undefined, 0] as [string | undefined, number],
  );
  return mostFrequent;
};

/** Get the document guids for `doc` and all if its children (recursively) */
export const getDocumentGuids = (doc: Autodesk.Viewing.BubbleNode): string[] => {
  const guid = doc.data.guid;
  const childrenGuids = doc.children.flatMap(getDocumentGuids);
  return [guid, ...childrenGuids];
};

export const spoolObjectProperties = [
  'Level',
  'Reference Level',
  'Quantity',
  'eVolve_UnitofMeasure',
  'Trade Size',
  'Manufacturer',
  'eVolve_Length',
] as const;

type SpoolObjectProperties = (typeof spoolObjectProperties)[number];

export const buildSpoolObject = <T extends string>(obj: TypeSafePropertyResult<SpoolObjectProperties | T>) => ({
  dbId: obj.dbId,
  name: obj.name ?? 'UNKNOWN',
  level: getObjectPropertyValueAsString(obj, 'Level', 'Constraints'),
  referenceLevel: getObjectPropertyValueAsString(obj, 'Reference Level'),
  quantity: getObjectPropertyValueAsNumber(obj, 'Quantity'),
  uomCode: getObjectPropertyValueAsString(obj, 'eVolve_UnitofMeasure'),
  tradeSize: getObjectPropertyValueAsString(obj, 'Trade Size'),
  manufacturer: getObjectPropertyValueAsString(obj, 'Manufacturer'),
  length: getObjectPropertyValueAsNumber(obj, 'eVolve_Length'),
  manufacturerCatalogNumber: undefined,
});

/** Helper method to get all dbIds from a spool. */
export const getDbIdsFromSpool = (spool: RenderableSpool) => spool.objects.map((obj) => obj.dbId);
